import { DataList, Flex, Text } from '@radix-ui/themes'
import cn from 'classnames'
import type { ReactNode } from 'react'
import { CopyButton } from '~/components/CopyButton'
import { SectionCard } from '~/components/SectionCard'
import { formatTimestamp } from '~/utils/date'
import { StationTypeIcon } from './StationTypeIcon'
import type { StationSectionProps } from '../types'

const humanize = (value: string): string => {
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

interface OverviewRowProps {
  label: string
  children: ReactNode
}

const OverviewRow = ({ label, children }: OverviewRowProps) => (
  <DataList.Item>
    <DataList.Label minWidth="150px">{label}</DataList.Label>
    <DataList.Value>{children}</DataList.Value>
  </DataList.Item>
)

interface TextRowProps {
  label: string
  value: string | null
  mono?: boolean
}

const TextRow = ({ label, value, mono }: TextRowProps) => (
  <OverviewRow label={label}>
    {value ? (
      <Text size="2" className={cn(mono && 'font-mono')}>
        {value}
      </Text>
    ) : (
      <Text size="2" color="gray">
        —
      </Text>
    )}
  </OverviewRow>
)

export const StationOverviewCard = ({ station }: StationSectionProps) => {
  const { volume } = station
  const description = station.description.trim()

  return (
    <SectionCard title="Overview">
      <DataList.Root>
        <TextRow label="Name" value={station.name} />
        {description ? <TextRow label="Description" value={description} /> : null}
        <TextRow label="Internal ID" value={station.internal_id} mono />

        <OverviewRow label="Measurement type">
          <Flex align="center" gap="2">
            <StationTypeIcon
              measurementType={station.measurement_type}
              stationType={volume?.station_type ?? null}
            />
            <Text size="2">{humanize(station.measurement_type)}</Text>
          </Flex>
        </OverviewRow>

        <OverviewRow label="Station UUID">
          <Flex align="center" gap="2">
            <Text size="2" className="font-mono">
              {station.id}
            </Text>
            <CopyButton value={station.id} label="Copy station UUID" />
          </Flex>
        </OverviewRow>

        <TextRow label="Created" value={formatTimestamp(station.created_at)} />

        {volume ? (
          <>
            <TextRow label="Station type" value={humanize(volume.station_type)} />
            <TextRow label="Water type" value={volume.water_type && humanize(volume.water_type)} />
            <TextRow label="Cost per m³" value={volume.cost_per_m3?.toString() ?? null} />
            {/* Calendar date, not an instant — formatting it shifts the day west of UTC. */}
            <TextRow label="Expected end of life" value={volume.expected_end_of_life} />
          </>
        ) : null}
      </DataList.Root>
    </SectionCard>
  )
}
